import React, { useRef, useState, useEffect } from "react";
import SimpleReactValidator from "simple-react-validator";
import { getAllStates, getDistricts } from "india-state-district";
import emailjs from "@emailjs/browser";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Paper,
} from "@mui/material";
import ConfirmationCard from "../../../components/ConfirmationCard/ConfirmationCard";
import {
  FaCloudUploadAlt,
  FaUser,
  FaGraduationCap,
  FaBriefcase,
} from "react-icons/fa";

const ApplyNowForm = () => {
  const formRef = useRef();
  const [forms, setForms] = useState({
    dept: "",
    office: "",
    name: "",
    stateCode: "",
    district: "",
    phone: "",
    age: "",
    email: "",
    gender: "",
    gradution: "",
    specilisation: "",
    postGradution: "",
    certification: "",
    currentPosition: "",
    currentOrganization: "",
    teamSize: "",
    carrerAchivements: "",
    resume: null,
  });

  const [submitted, setSubmitted] = useState(false);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ show: false, success: false });

  const [, forceUpdate] = useState();
  const [validator, setValidator] = useState(
    new SimpleReactValidator({
      className: "text-danger small mt-1",
      messages: {
        required: "This field is required.",
        email: "Invalid email.",
        phone: "Invalid phone.",
      },
    })
  );

  useEffect(() => {
    setStates(getAllStates());
  }, []);

  useEffect(() => {
    if (forms.stateCode) {
      setDistricts(getDistricts(forms.stateCode));
    }
  }, [forms.stateCode]);

  const changeHandler = (e) => {
    const { name, value } = e.target;

    // Strict input filtering
    let filteredValue = value;
    if (["name", "gradution", "specilisation"].includes(name)) {
      filteredValue = value.replace(/[^a-zA-Z\s]/g, "");
    } else if (name === "phone") {
      filteredValue = value.replace(/[^0-9]/g, "").slice(0, 10);
    } else if (name === "age" || name === "teamSize") {
      filteredValue = value.replace(/[^0-9]/g, "");
    }

    setForms((prev) => ({ ...prev, [name]: filteredValue }));
    validator.showMessageFor(name);
    forceUpdate(1);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1024 * 1024) {
        // 1MB limit
        alert("File size should be less than 1MB");
        return;
      }
      setForms((prev) => ({ ...prev, resume: file }));
    }
  };

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      if (file) {
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      } else {
        resolve("");
      }
    });

  const submitHandler = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    if (validator.allValid() && forms.gender) {
      setLoading(true);
      try {
        await emailjs.send(
          "service_meb7bls",
          "template_k5wobpe",
          { ...forms },
          "ox3xNBcvFvURJ0mTY"
        );

        setForms({
          dept: "",
          office: "",
          name: "",
          stateCode: "",
          district: "",
          phone: "",
          age: "",
          email: "",
          gender: "",
          gradution: "",
          specilisation: "",
          postGradution: "",
          certification: "",
          currentPosition: "",
          currentOrganization: "",
          teamSize: "",
          carrerAchivements: "",
        });
        setLoading(false);
        setSubmitted(false);
        setModal({ show: true, success: true });
      } catch (error) {
        setLoading(false);
        setModal({ show: true, success: false });
      }
    } else {
      validator.showMessages();
      forceUpdate(1);
    }
  };

  const departments = [
    "Accounts and regulatory/ MIS",
    "Audit and Assurance",
    "Administration",
    "Business Development",
    "Company Law and Secretary",
    "Corporate Law and Commercial",
    "Direct Tax",
    "Finance And Accounts",
    "Human Resources",
    "Indirect Tax",
    "Information Technology",
    "International Business Consulting",
    "Market Entry and Business Consulting",
    "Marketing and Communication",
    "Transfer Pricing",
    "Other",
  ];

  const offices = ["Pune", "Mumbai", "Bangalore", "Gurugram"];

  const sectionHeader = (icon, title) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, mt: 2 }}>
      <Box
        sx={{
          p: 1,
          borderRadius: "8px",
          bgcolor: "rgba(26, 66, 161, 0.1)",
          color: "var(--primary-color)",
          display: "flex",
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: "var(--primary-color)",
          fontFamily: "'Roboto Slab', serif",
        }}
      >
        {title}
      </Typography>
    </Box>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, md: 6 },
        borderRadius: "24px",
        borderTop: "8px solid var(--primary-color)",
        boxShadow: "0 30px 60px rgba(0,0,0,0.1)",
      }}
    >
      <form onSubmit={submitHandler} ref={formRef}>
        {/* Step 1: Personal Details */}
        {sectionHeader(<FaUser size={20} />, "Personal Details")}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Select Department"
              name="dept"
              value={forms.dept}
              onChange={changeHandler}
              error={submitted && !validator.fieldValid("dept")}
            >
              {departments.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>
            {submitted && validator.message("dept", forms.dept, "required")}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Preferred Office"
              name="office"
              value={forms.office}
              onChange={changeHandler}
              error={submitted && !validator.fieldValid("office")}
            >
              {offices.map((o) => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </TextField>
            {submitted && validator.message("office", forms.office, "required")}
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Applicant Full Name"
              name="name"
              value={forms.name}
              onChange={changeHandler}
              error={submitted && !validator.fieldValid("name")}
            />
            {submitted &&
              validator.message("name", forms.name, "required|alpha_space")}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="State"
              name="stateCode"
              value={forms.stateCode}
              onChange={changeHandler}
              error={submitted && !validator.fieldValid("stateCode")}
            >
              {states.map((s) => (
                <MenuItem key={s.code} value={s.code}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
            {submitted &&
              validator.message("stateCode", forms.stateCode, "required")}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="City"
              name="district"
              value={forms.district}
              onChange={changeHandler}
              disabled={!forms.stateCode}
              error={submitted && !validator.fieldValid("district")}
            >
              {districts.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>
            {submitted &&
              validator.message("district", forms.district, "required")}
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              value={forms.email}
              onChange={changeHandler}
              error={submitted && !validator.fieldValid("email")}
            />
            {submitted &&
              validator.message("email", forms.email, "required|email")}
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={forms.phone}
              onChange={changeHandler}
              error={submitted && !validator.fieldValid("phone")}
            />
            {submitted &&
              validator.message(
                "phone",
                forms.phone,
                "required|numeric|min:10|max:10"
              )}
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label="Age"
              name="age"
              value={forms.age}
              onChange={changeHandler}
              error={submitted && !validator.fieldValid("age")}
            />
            {submitted &&
              validator.message("age", forms.age, "required|numeric")}
          </Grid>
          <Grid item xs={12}>
            <FormControl
              component="fieldset"
              error={submitted && !forms.gender}
            >
              <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                Gender
              </FormLabel>
              <RadioGroup
                row
                name="gender"
                value={forms.gender}
                onChange={changeHandler}
              >
                {["Male", "Female", "Others"].map((g) => (
                  <FormControlLabel
                    key={g}
                    value={g}
                    control={
                      <Radio
                        sx={{
                          color: "var(--primary-color)",
                          "&.Mui-checked": { color: "var(--primary-color)" },
                        }}
                      />
                    }
                    label={g}
                  />
                ))}
              </RadioGroup>
              {submitted &&
                validator.message("gender", forms.gender, "required")}
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ my: 5 }} />

        {/* Step 2: Educational Details */}
        {sectionHeader(
          <FaGraduationCap size={20} />,
          "Educational Qualification"
        )}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Graduation"
              name="gradution"
              value={forms.gradution}
              onChange={changeHandler}
              error={submitted && !validator.fieldValid("gradution")}
            />
            {submitted &&
              validator.message(
                "gradution",
                forms.gradution,
                "required|alpha_space"
              )}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Specialisation"
              name="specilisation"
              value={forms.specilisation}
              onChange={changeHandler}
              error={submitted && !validator.fieldValid("specilisation")}
            />
            {submitted &&
              validator.message(
                "specilisation",
                forms.specilisation,
                "required|alpha_space"
              )}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Post Graduation (Optional)"
              name="postGradution"
              value={forms.postGradution}
              onChange={changeHandler}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Other Certifications"
              name="certification"
              value={forms.certification}
              onChange={changeHandler}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 5 }} />

        {/* Step 3: Professional Details */}
        {sectionHeader(<FaBriefcase size={20} />, "Professional Details")}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Current Position"
              name="currentPosition"
              value={forms.currentPosition}
              onChange={changeHandler}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Current Organization"
              name="currentOrganization"
              value={forms.currentOrganization}
              onChange={changeHandler}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Direct Team Size managed"
              name="teamSize"
              value={forms.teamSize}
              onChange={changeHandler}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Key Career Achievements"
              name="carrerAchivements"
              value={forms.carrerAchivements}
              onChange={changeHandler}
              multiline
              rows={1}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 6, display: "flex", justifyContent: "center" }}>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              bgcolor: "var(--primary-color)",
              color: "#fff",
              px: 10,
              py: 2,
              borderRadius: "12px",
              fontSize: "1.1rem",
              fontWeight: 700,
              boxShadow: "0 10px 30px rgba(26, 66, 161, 0.3)",
              textTransform: "none",
              "&:hover": {
                bgcolor: "var(--primary-color)",
                filter: "brightness(1.1)",
                transform: "translateY(-2px)",
              },
            }}
          >
            {loading ? "Submitting Application..." : "Submit Application"}
          </Button>
        </Box>
      </form>

      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.6)",
            zIndex: 1050,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box className="spinner-border text-light" role="status" />
        </Box>
      )}

      <ConfirmationCard modal={modal} setModal={setModal} />
    </Paper>
  );
};

export default ApplyNowForm;
